"use client";

import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { Button } from "@/ui/components/Button";
import { Skeleton } from "@/ui/components/Skeleton";
import { useCandidateProfile } from "@/lib/signals/useCandidateProfile";
import type { CandidateProfile, ProfileFact } from "@/lib/signals/types";
import styles from "./SignalsView.module.css";

const SOURCE_LABEL = { cv: "CV", web: "Web", research: "DOTS research", record: "Floats record" } as const;
const PREVIEW = 12;

/** Short values read best as chips; longer ones as a list. */
const isShort = (facts: ProfileFact[]) => facts.every((fact) => fact.value.length <= 40);

function sourceText(fact: ProfileFact) {
  return [...new Set(fact.sources.map((source) => SOURCE_LABEL[source.kind] ?? source.kind))].join(", ");
}

function FactValue({ fact }: { fact: ProfileFact }) {
  const web = fact.sources.find((source) => source.kind === "web" && source.url);
  return web?.url
    ? <a className={styles.sourceLink} href={web.url} target="_blank" rel="noopener noreferrer" title={`From ${web.title ?? web.url}`}>{fact.value}<ExternalLink size={12} aria-hidden /></a>
    : <span title={`From ${sourceText(fact)}`}>{fact.value}</span>;
}

function Group({ label, facts }: { label: string; facts: ProfileFact[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? facts : facts.slice(0, PREVIEW);
  return <div className={styles.factGroup}>
    <h5>{label}<span className={styles.muted}> · {facts.length}</span></h5>
    {isShort(facts)
      ? <ul className={styles.factChips}>{shown.map((fact, index) => <li key={index} className={fact.sources.some((source) => source.kind === "web") ? styles.factWeb : undefined}><FactValue fact={fact} /></li>)}</ul>
      : <ul className={styles.factList}>{shown.map((fact, index) => <li key={index}><FactValue fact={fact} /> <span className={styles.muted}>· {sourceText(fact)}</span></li>)}</ul>}
    {facts.length > PREVIEW && <Button variant="tertiary" size="sm" onClick={() => setAll(!all)}>{all ? "Show fewer" : `Show all ${facts.length}`}</Button>}
  </div>;
}

export function KnowledgeView({ profile }: { profile: CandidateProfile }) {
  const counts = profile.sourceCounts ?? {};
  const from = (["cv", "research", "web", "record"] as const).filter((kind) => counts[kind]).map((kind) => `${SOURCE_LABEL[kind]} ${counts[kind]}`).join(" · ");
  return <section className={styles.dossier} aria-labelledby="knowledge-heading">
    <header className={styles.dossierHeader}>
      <h3 id="knowledge-heading">What DOTS knows</h3>
      <p className={styles.muted}>{profile.factCount} facts{from && ` · from ${from}`}. Web facts link to their page.</p>
    </header>
    {(profile.sections ?? []).map((section) => <div key={section.id} className={styles.dossierSection}>
      <h4>{section.title}</h4>
      {(section.groups ?? []).map((group) => <Group key={`${section.id}:${group.label}`} label={group.label} facts={group.facts ?? []} />)}
    </div>)}
  </section>;
}

export function CandidateKnowledge({ candidateId }: { candidateId: string }) {
  const query = useCandidateProfile(candidateId);
  if (query.isPending) return <div className={styles.dossier} role="status" aria-label="Loading what DOTS knows"><Skeleton variant="rounded" height={200} /></div>;
  if (query.isError) return <p className={styles.muted} role="alert">What DOTS knows couldn’t be loaded. <Button variant="tertiary" size="sm" onClick={() => void query.refetch()}>Try again</Button></p>;
  if (!query.data || query.data.factCount === 0) return <p className={styles.muted}>DOTS hasn’t extracted anything about this candidate yet. It reads the CV when it’s uploaded.</p>;
  return <KnowledgeView profile={query.data} />;
}
